import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useSWRConfig } from 'swr'

import { useToastContext } from '../../../../contexts'
import { getHandleableError } from '../../../../utils/errors/isXiaError'
import { useCurrentTenantConfig } from '../../../../utils/tenants'
import { useAllSuppliers } from '../../suppliers/data'
import { SupplierDetailWithTemaplate } from '../data'
import { requestCreateSupplier } from '../data/api'
import { AddSupplierFormDto } from '../data/model'
import { AddSupplierForm } from './AddSupplier.form'

export type FormFieldName = keyof AddSupplierFormDto

export const AddSupplierData: React.FC = () => {
  const { data: suppliers, mutate } = useAllSuppliers()
  const { showToast } = useToastContext()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { mutate: globalMutate } = useSWRConfig()
  const { tenantId } = useCurrentTenantConfig()

  const [isRequestRunning, setIsRequestRunning] = useState(false)

  function formValuesToSupplierDetail(
    values: AddSupplierFormDto,
  ): SupplierDetailWithTemaplate {
    return {
      Id: values.id,
      Template: values.template,
      Name: values.name,
      Description: values.description,
      Status: 'active',
      EmailSettings: {
        Inbound: {
          Value: values.inboundValue,
          OverrideDefaultValue: values.inboundValue ? true : false,
        },
        Outbound: {
          Value: values.outboundValue,
          OverrideDefaultValue: values.outboundValue ? true : false,
        },
        Support: {
          Value: values.supportValue,
          OverrideDefaultValue: values.supportValue ? true : false,
        },
      },
    }
  }

  const initialData: AddSupplierFormDto = {
    id: '',
    template: '',
    name: '',
    description: '',
    inboundValue: '',
    outboundValue: '',
    supportValue: '',
  }

  async function handleSubmit(formValues: AddSupplierFormDto) {
    const details = formValuesToSupplierDetail(formValues)

    try {
      setIsRequestRunning(true)

      await requestCreateSupplier(details)

      mutate()
      globalMutate((key: string) =>
        key.toLowerCase().startsWith(`/tenants/${tenantId}/projects`),
      )

      // show success toast
      showToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t('supplier.title.add.add.messages.success.summary', {
          supplier: formValues.name,
        }),
      })

      navigate(`/suppliers/${formValues.id}`)
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorTitle = handleableError.title
      let errorMessage = handleableError.message

      // show error toast
      showToast({
        style: {},
        closable: true,
        severity: 'error',
        sticky: true,
        summary: errorTitle || t('errors.generic.unknown.title'),
        detail: errorMessage || t('errors.generic.unknown.detail'),
      })
      setIsRequestRunning(false)
    }
  }

  return (
    <AddSupplierForm
      initialData={initialData}
      isRequestRunning={isRequestRunning}
      suppliers={suppliers}
      handleSubmit={handleSubmit}
    />
  )
}
